/**
 * A `fetch`-shaped request over `XMLHttpRequest`, for the one thing `fetch` cannot do: report upload
 * progress (F20-h Wave 1 contract client-side fact 1; F20-h WI-1.2 plan Decision 1). `apiClient`'s
 * `postFormWithProgress` hands `sendWithXhr` to its shared `request()` in place of `fetch`, so the base URL,
 * the CSRF echo and its priming, the `401`/`403` handlers and `ApiError` stay the code every other
 * call runs; this file only moves bytes and turns the answer into a standard `Response`.
 *
 * It behaves as `fetch` does where `request()` relies on it:
 *
 * - `credentials: 'include'` becomes `withCredentials`, so the `SESSION` cookie rides along on the
 *   cross-site call to `api.<env>` (AD #33);
 * - every header `request()` built (`X-XSRF-TOKEN` among them) is set on the request;
 * - an aborted signal rejects with `signal.reason` and aborts the request;
 * - a network error or a timeout rejects with a `TypeError`.
 */

/** Upload progress in bytes: `loaded` of `total`. */
export interface UploadProgress {
    readonly loaded: number;
    readonly total: number;
}

/**
 * The part of `XMLHttpRequest` this transport uses — `XMLHttpRequest` itself satisfies it, and a test
 * can supply a small fake without a cast.
 */
export interface XhrLike {
    open(method: string, url: string): void;
    setRequestHeader(name: string, value: string): void;
    withCredentials: boolean;
    readonly upload: { onprogress: ((event: ProgressEvent) => void) | null };
    onload: ((event: ProgressEvent) => void) | null;
    onerror: ((event: ProgressEvent) => void) | null;
    onabort: ((event: ProgressEvent) => void) | null;
    ontimeout: ((event: ProgressEvent) => void) | null;
    send(body?: XMLHttpRequestBodyInit | null): void;
    abort(): void;
    readonly status: number;
    readonly statusText: string;
    readonly responseText: string;
    getAllResponseHeaders(): string;
}

/** What `apiClient`'s `request()` calls: `fetch`'s shape, narrowed to the URL string it passes. */
export type Transport = (url: string, init: RequestInit) => Promise<Response>;

/** Statuses whose response has no body by definition; `new Response` refuses a body for them. */
const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

/** `getAllResponseHeaders()`'s `name: value` lines as `Headers`. */
function parseHeaders(raw: string): Headers {
    const headers = new Headers();
    for (const line of raw.split(/\r?\n/)) {
        const colon = line.indexOf(':');
        if (colon > 0) {
            headers.append(line.slice(0, colon).trim(), line.slice(colon + 1).trim());
        }
    }
    return headers;
}

/** The body as XHR takes it. A stream has no XHR form; no caller sends one. */
function xhrBody(body: BodyInit | null | undefined): XMLHttpRequestBodyInit | null {
    if (body === null || body === undefined) {
        return null;
    }
    if (body instanceof ReadableStream) {
        throw new TypeError('A stream body cannot be sent through XMLHttpRequest');
    }
    return body;
}

/**
 * A transport that sends through the XHR `createXhr` makes and reports upload progress to
 * `onUploadProgress` whenever the browser knows the total.
 */
export function sendWithXhr(
    createXhr: () => XhrLike,
    onUploadProgress?: (progress: UploadProgress) => void,
): Transport {
    return (url, init) =>
        new Promise<Response>((resolve, reject) => {
            const signal = init.signal ?? undefined;
            if (signal?.aborted) {
                reject(signal.reason);
                return;
            }
            let body: XMLHttpRequestBodyInit | null;
            try {
                body = xhrBody(init.body);
            } catch (error: unknown) {
                reject(error);
                return;
            }
            const xhr = createXhr();
            let settled = false;
            const onAbortSignal = (): void => {
                if (!settled) {
                    settled = true;
                    reject(signal?.reason);
                    xhr.abort();
                }
            };
            const finish = (): void => {
                settled = true;
                signal?.removeEventListener('abort', onAbortSignal);
            };
            const fail = (message: string): void => {
                if (!settled) {
                    finish();
                    reject(new TypeError(message));
                }
            };

            xhr.open(init.method ?? 'GET', url);
            xhr.withCredentials = init.credentials === 'include';
            new Headers(init.headers).forEach((value, name) => {
                xhr.setRequestHeader(name, value);
            });
            if (onUploadProgress !== undefined) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        onUploadProgress({ loaded: event.loaded, total: event.total });
                    }
                };
            }
            xhr.onload = () => {
                if (settled) {
                    return;
                }
                const { status } = xhr;
                if (status < 200 || status > 599) {
                    // A status of 0 is a request the browser blocked or lost: fetch's network error.
                    fail('Network request failed');
                    return;
                }
                finish();
                resolve(
                    new Response(NULL_BODY_STATUSES.has(status) ? null : xhr.responseText, {
                        status,
                        statusText: xhr.statusText,
                        headers: parseHeaders(xhr.getAllResponseHeaders()),
                    }),
                );
            };
            xhr.onerror = () => fail('Network request failed');
            xhr.ontimeout = () => fail('Network request timed out');
            xhr.onabort = () => fail('Network request aborted');
            signal?.addEventListener('abort', onAbortSignal, { once: true });
            xhr.send(body);
        });
}
